<script setup lang="ts">
import { ref, type Ref } from 'vue';
import { RouterLink, RouterView } from 'vue-router';
import { WalletMultiButton } from 'solana-wallets-vue';

import { goTo } from '@/helpers/utilities';

// Define props.
const props = defineProps<{
    class?: any;
}>();

// Type for menu text handler.
type MenuTextHandler = {
    assend: boolean;
    customize: boolean;
    mining: boolean;
    hatchening: boolean;
    explorer: boolean;
    buy: boolean;
    history: boolean;
    team: boolean;
};

// Grab the auction.
// const props = defineProps<{ auction?: string | null }>();

// Constants and important external URLs.
const bugReports = `https://airtable.com/embed/shraSrLJRB5ArttZ7?backgroundColor=green`;

// Define any reactive variables or states here.
const walletMenuIsOpen: Ref<boolean> = ref(false);
const m: Ref<MenuTextHandler> = ref({
    assend: false,
    customize: false,
    mining: false,
    hatchening: false,
    explorer: false,
    buy: false,
    history: false,
    team: false,
});

// Function to toggle menu text.
const toggleMenuText = (item: keyof MenuTextHandler, state: boolean) => {
    console.log(`toggling ${item} ${state}`);
    m.value[item] = state;
};

// Define any reactive variables or states here.
const menuIsOpen: Ref<boolean> = ref(false);

// Functions to open menu screens.
const toggleMenu = () => (menuIsOpen.value = !menuIsOpen.value);
const toggleWalletMenu = () =>
    (walletMenuIsOpen.value = !walletMenuIsOpen.value);
</script>

<template>
    <header id="top-header" :class="props.class">
        <div id="ah-header" class="header-items">
            <div class="header-items-left">
                <div class="logo make-purple">
                    <RouterLink to="/">
                        <img src="@/assets/logos/hs.svg" />
                    </RouterLink>
                </div>
                <div class="ah-icons-divider"></div>
                <div
                    class="menu-icon make-purple"
                    @click="() => goTo(`https://upgrade.daa.itsboots.xyz/`)"
                    @mouseenter="() => toggleMenuText(`assend`, true)"
                    @mouseleave="() => toggleMenuText(`assend`, false)"
                >
                    <div :class="`menu-text ${m.assend ? `is-active` : ``}`">
                        Assend
                    </div>
                    <img src="@/assets/landing/icons/Burn.svg" />
                </div>
                <div
                    class="menu-icon make-purple"
                    @click="
                        () => goTo(`https://itsboots.xyz/collections/daa2/`)
                    "
                    @mouseenter="() => toggleMenuText(`customize`, true)"
                    @mouseleave="() => toggleMenuText(`customize`, false)"
                >
                    <div :class="`menu-text ${m.customize ? `is-active` : ``}`">
                        Customize
                    </div>
                    <img src="@/assets/landing/icons/Customize.svg" />
                </div>
                <RouterLink to="/mining">
                    <div
                        class="menu-icon make-purple"
                        @mouseenter="() => toggleMenuText(`mining`, true)"
                        @mouseleave="() => toggleMenuText(`mining`, false)"
                    >
                        <div
                            :class="`menu-text ${m.mining ? `is-active` : ``}`"
                        >
                            Mining
                        </div>
                        <img src="@/assets/landing/icons/Mining.svg" />
                    </div>
                </RouterLink>
                <RouterLink to="/explorer">
                    <div
                        class="menu-icon make-purple"
                        @mouseenter="() => toggleMenuText(`explorer`, true)"
                        @mouseleave="() => toggleMenuText(`explorer`, false)"
                    >
                        <div
                            :class="`menu-text ${
                                m.explorer ? `is-active` : ``
                            }`"
                        >
                            Explorer
                        </div>
                        <img src="@/assets/landing/icons/Explorer.svg" />
                    </div>
                </RouterLink>
                <div
                    class="menu-icon make-purple"
                    @click="
                        () =>
                            goTo(
                                `https://magiceden.io/creators/degenerate_ape_academy`
                            )
                    "
                    @mouseenter="() => toggleMenuText(`buy`, true)"
                    @mouseleave="() => toggleMenuText(`buy`, false)"
                >
                    <div :class="`menu-text ${m.buy ? `is-active` : ``}`">
                        Buy
                    </div>
                    <img src="@/assets/landing/icons/Buy.svg" />
                </div>
                <div
                    class="menu-icon make-purple"
                    @click="() => goTo(`https://history.degenape.academy/`)"
                    @mouseenter="() => toggleMenuText(`history`, true)"
                    @mouseleave="() => toggleMenuText(`history`, false)"
                >
                    <div :class="`menu-text ${m.history ? `is-active` : ``}`">
                        History
                    </div>
                    <img src="@/assets/landing/icons/History.svg" />
                </div>
                <div
                    class="menu-icon menu-disabled make-purple"
                    @mouseenter="() => toggleMenuText(`team`, true)"
                    @mouseleave="() => toggleMenuText(`team`, false)"
                >
                    <div :class="`menu-text ${m.team ? `is-active` : ``}`">
                        Team
                    </div>
                    <img src="@/assets/landing/icons/Team.svg" />
                </div>
            </div>
            <div class="w-mobile">
                <div class="logo make-purple">
                    <RouterLink to="/">
                        <img src="@/assets/logos/hs.svg" />
                    </RouterLink>
                </div>
                <wallet-multi-button dark></wallet-multi-button>
                <div class="hamburger flex-center">
                    <div
                        class="hamburger-stack"
                        :class="{ close: menuIsOpen }"
                        @click="toggleMenu"
                    >
                        <span
                            v-for="lineNum of [1, 2, 3]"
                            v-bind:key="lineNum"
                            :class="`line line0${lineNum} white`"
                        ></span>
                    </div>
                </div>
            </div>
            <div class="header-items-right">
                <div class="w-desktop">
                    <div class="bug" @click="goTo(`https://hodlers.app/`)">
                        <img src="@/assets/hodlers.png" />
                    </div>
                </div>
                <div class="w-desktop">
                    <div class="bug" @click="goTo(bugReports)">
                        <img src="@/assets/bug.png" />
                    </div>
                </div>
                <div class="w-desktop">
                    <wallet-multi-button dark></wallet-multi-button>
                </div>
            </div>
        </div>
        <Transition>
            <div v-if="walletMenuIsOpen" class="wallet-menu-container">
                <div class="wallet-menu">
                    <div class="close-wallet-menu" @click="toggleWalletMenu()">
                        Close
                    </div>
                </div>
            </div>
        </Transition>
        <Transition>
            <div v-if="menuIsOpen" class="menu" @click="toggleMenu">
                <div class="menu-select">
                    <div class="menu-select-column">
                        <div
                            class="menu-selection"
                            @click="goTo(`https://upgrade.daa.itsboots.xyz/`)"
                        >
                            ASSEND
                        </div>
                        <div
                            class="menu-selection"
                            @click="
                                goTo(`https://itsboots.xyz/collections/daa2/`)
                            "
                        >
                            CUSTOMIZE
                        </div>
                        <div class="menu-selection">
                            <RouterLink :to="{ name: 'mining' }"
                                >MINING</RouterLink
                            >
                        </div>
                        <div class="menu-selection">
                            <RouterLink :to="{ name: 'explorer' }"
                                >EXPLORER</RouterLink
                            >
                        </div>
                        <div
                            class="menu-selection"
                            @click="
                                goTo(
                                    `https://magiceden.io/creators/degenerate_ape_academy`
                                )
                            "
                        >
                            BUY
                        </div>
                        <div
                            class="menu-selection"
                            @click="goTo(`https://history.degenape.academy/`)"
                        >
                            HISTORY
                        </div>
                    </div>
                </div>
                <div class="menu-bottom">
                    <div class="social">
                        <div
                            class="social-item make-purple"
                            @click="goTo(`https://twitter.com/DegenApeAcademy`)"
                        >
                            <font-awesome-icon
                                icon="fa-brands fa-twitter"
                                size="2x"
                            />
                        </div>
                        <div
                            class="social-item make-purple"
                            @click="
                                goTo(
                                    `https://discord.com/invite/degenapeacademy`
                                )
                            "
                        >
                            <font-awesome-icon
                                icon="fa-brands fa-discord"
                                size="2x"
                            />
                        </div>
                    </div>
                    <div
                        class="privacy-policy"
                        @click="goTo(`/privacy/DAA_Club_Terms_of_Service.pdf`)"
                    >
                        Privacy Policy & Terms of Service
                    </div>
                </div>
            </div>
        </Transition>
    </header>
    <RouterView />
</template>

<style>
@import '@/styles/base.css';

.swv-mobile-wallet {
    display: none;
}

.menu-text {
    position: absolute;
    font-size: 12px;
    font-family: 'Bicyclette Bold', Helvetica, Arial;
    text-transform: uppercase;
    top: 35px;
    opacity: 0;
    transform: translateY(-5px);
    transition:
        0.2s opacity ease,
        0.2s transform ease;
    pointer-events: none;
}

.is-active {
    opacity: 1;
    transform: translateY(0px);
}

.make-purple {
    filter: invert(52%) sepia(65%) saturate(5533%) hue-rotate(338deg)
        brightness(100%) contrast(92%);
    transition:
        0.2s transform cubic-bezier(0.34, 1.56, 0.64, 1),
        0.3s filter ease-in-out;
    cursor: pointer;
}

/* .make-purple {
    filter: brightness(0) saturate(100%) invert(79%) sepia(53%) saturate(5612%)
        hue-rotate(211deg) brightness(104%) contrast(98%);
    transition:
        0.2s transform cubic-bezier(0.34, 1.56, 0.64, 1),
        0.3s filter ease-in-out;
    cursor: pointer;
} */

.danger-text {
    position: absolute;
    width: 70px;
    height: 18px;
    background-color: yellow;
    filter: hue-rotate(10deg);
    color: black;
    font-size: 12px;
    font-family: montserrat;
    font-weight: 800;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10;
    transform: rotate(-20deg);
}

.make-purple:hover {
    transform: scale(1.1);
    filter: grayscale(1) brightness(5);
}

.menu-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 30px;
    width: 44px;
}

.w-desktop {
    display: block;
}

.menu-icon img {
    height: 25px;
}

.menu-disabled {
    opacity: 0.5;
    cursor: not-allowed;
    filter: grayscale(1);
}

.header-items-left {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    z-index: 10;
    pointer-events: all;
}

.ah-icons-divider {
    width: 40px;
    height: 25px;
    border-right: 2px solid #ab94fc33;
}

.swv-button {
    width: 170px;
}

.swv-button-icon,
.swv-button-icon img {
    padding-bottom: 1px;
}

.menu-select-column .disabled {
    cursor: not-allowed;
    opacity: 0.25;
}

.menu-select-column .disabled:hover {
    transform: scale(1);
    color: rgba(255, 255, 255, 0.5);
}

.header-items-right {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    pointer-events: all;
    margin-right: 12px;
}

.swv-button:hover {
    transform: scale(1);
}

.swv-modal-wrapper .swv-button:not([disabled]):hover {
    background: none;
}

.swv-dark .swv-button:not([disabled]):hover {
    background-color: rgba(98, 98, 98, 0.4) !important;
}

.swv-modal-collapse-button {
    font-family: Montserrat !important;
    font-weight: 700 !important;
    font-style: normal !important;
    font-size: 13px !important;
    border-radius: 0 0 8px 8px !important;
}

.swv-modal-list .swv-button-icon,
.swv-modal-list .swv-button-icon img {
    height: 22px;
    width: 22px;
}

.swv-modal-list .swv-button p {
    font-family: Montserrat;
    font-weight: 800;
    padding-bottom: 3px;
    color: #fff;
}

.swv-modal-wrapper .swv-button {
    color: #fff;
}

.swv-button p {
    font-family: 'BN Boop', Helvetica, Arial;
    font-weight: 300;
    font-style: normal;
    font-size: 10px;
}

.swv-modal-list li {
    padding: 6px 20px;
}

.swv-modal-title {
    font-family: 'BN Boop', Helvetica, Arial;
    font-size: 23px;
    font-weight: 400;
    line-height: 15px;
    letter-spacing: -0.03em;
    text-align: center;
    margin-top: 40px;
    color: #fff;
}

.swv-modal-button-close {
    height: auto;
    width: auto;
    background: none;
    color: #c6b7ff;
    top: 9px;
    right: 9px;
}

.swv-modal-list .swv-button .swv-wallet-status {
    font-size: 12px;
    font-weight: 800;
    text-transform: uppercase;
    color: #c6b7ff;
    padding-bottom: 3px;
}

.swv-wallet-status {
    font-family: 'Montserrat', sans-serif;
}

.swv-modal-wrapper {
    background-color: #000000aa;
}

.swv-dark .swv-button {
    height: 42px;
    text-align: center;
    display: flex;
    justify-content: center;
    align-items: center;
    font-family: 'BN Boop', Helvetica, Arial;
    font-size: 12px;
    font-weight: 400;
    background: #ffffff11;
    border-radius: 10px;
    padding-top: 3px;
}

.swv-dark .swv-button[disabled] {
    font-size: 10px;
    color: rgba(255, 255, 255, 0);
}

.swv-dark .swv-button-trigger {
    background-color: rgba(0, 0, 0, 0.3);
    border: 2px solid white;
}

.wallet-menu-container {
    position: fixed;
    width: 100vw;
    height: 100vh;
    padding: 30px;
    z-index: 11;
    top: 0;
    left: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    backdrop-filter: blur(10px) brightness(20%);
    -webkit-backdrop-filter: blur(10px) contrast(20%);
}

.wallet-menu {
    width: 100%;
    height: 60%;
    border-radius: 10px;
    background-color: #282828;
    filter: drop-shadow(20px 20px 20px rgba(10, 10, 10, 0.6));
}

.apeintosh-button-container {
    height: 40px;
    width: 180px;
    background: linear-gradient(
        180deg,
        #fdffff 0%,
        #9e8dcd 25.83%,
        #8578a9 88.55%,
        #71688c 100%
    );
    border: 2px solid black;
    border-radius: 10px;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 2px;
}

header {
    position: fixed;
    width: 100vw;
    top: 0;
    padding: 30px;
    z-index: 16;
    pointer-events: none;
}

.menu {
    position: fixed;
    width: 100vw;
    height: 100vh;
    background: radial-gradient(#3f3c37, #1f1c1a);
    z-index: 15;
    font-size: 26px;
    font-weight: normal;
}

.menu-select {
    display: flex;
    flex-direction: row;
    justify-content: space-around;
    align-items: center;
    width: 100%;
    height: 85%;
    color: rgba(255, 255, 255, 0.5);
    padding: 0 5%;
}

.menu-select-column {
    display: flex;
    flex-direction: column;
    justify-content: space-evenly;
    align-items: center;
    height: 60%;
}

.menu-bottom {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    height: 15%;
    bottom: 5%;
}

.social {
    display: flex;
    bottom: 40px;
    font-size: 20px;
}

.social-item {
    padding: 0 20px;
    transition: 0.2s transform cubic-bezier(0.34, 1.56, 0.64, 1);
    cursor: pointer;
}

.social-item:hover {
    transform: scale(1.4);
    filter: brightness(0) saturate(100%) invert(72%) sepia(41%) saturate(478%)
        hue-rotate(2deg) brightness(100%) contrast(93%);
}

.menu-selection {
    padding: 1% 0;
    transition:
        0.2s transform cubic-bezier(0.34, 1.56, 0.64, 1),
        0.2s color cubic-bezier(0.34, 1.56, 0.64, 1);
    cursor: pointer;
}

.menu-selection:hover {
    color: #fbe271;
    transform: scale(1.5);
}

.privacy-policy {
    font-family: 'Montserrat', sans-serif;
    font-size: 15px;
    font-weight: 500;
    transition:
        0.2s transform cubic-bezier(0.34, 1.56, 0.64, 1),
        0.2s color cubic-bezier(0.34, 1.56, 0.64, 1);
    cursor: pointer;
}

.privacy-policy:hover {
    color: #fbe271;
    transform: scale(1.2);
}

.logo {
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    transition: 0.2s transform cubic-bezier(0.34, 1.56, 0.64, 1);
    z-index: 10;
    pointer-events: all;
}

.logo a {
    display: contents;
}

.logo img {
    height: 50px;
}

.logo:hover {
    transform: scale(1.05);
    filter: grayscale(1) brightness(5);
}

.w-mobile {
    pointer-events: all;
}

.header-items {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    z-index: 2;
    transition: 0.5s opacity ease;
    pointer-events: none;
}

.bug {
    width: 52px;
    height: 42px;
    margin: 0 30px 0 0;
    border-radius: 10px;
    text-decoration: none;
    color: #1f1c1a;
    background-color: #ff2525;
    border: none;
    cursor: pointer;
    transition:
        0.4s transform cubic-bezier(0.34, 1.56, 0.64, 1),
        0.25s background-color ease-in-out;
    display: flex;
    justify-content: center;
    align-items: center;
}

.bug:hover {
    transform: scale(1.05);
    background-color: #c6b7ff;
}

.bug img {
    width: 20px;
}

.connect-wallet {
    font-size: 12px;
    line-height: 32px;
    text-align: center;
    padding: 0 1.5em;
    height: 32px;
    background-color: rgba(0, 0, 0, 0.3);
    color: #fff;
    border: 1px solid #7c7c7c;
    border-radius: 100px;
    transition:
        0.2s background-color cubic-bezier(0.34, 1.56, 0.64, 1),
        0.2s transform cubic-bezier(0.34, 1.56, 0.64, 1);
    cursor: pointer;
    width: 160px;
}

.connect-wallet:hover {
    background-color: rgba(255, 255, 255, 0.2);
    transform: scale(1.05);
}

.connect-wallet.connected {
    font-weight: 500;
}

.navigation {
    display: none;
}

.swv-modal-list .swv-button {
    justify-content: space-between;
}

/* Hamburger animation */
.hamburger-stack {
    width: 32px;
    height: 32px;
    margin: 0 4px 0 4px;
    position: relative;
    transition: 0.2s transform cubic-bezier(0.34, 1.56, 0.64, 1);
    cursor: pointer;
}

.hamburger-stack:hover {
    transform: scale(1.1);
}

.line {
    position: absolute;
    height: 2px;
    width: 100%;
    border-radius: 10px;
    transition: all cubic-bezier(0.25, 0.1, 0.28, 1.54) 0.32s;
}

.white {
    background: #ff1d1d;
}

.black {
    background: black;
}

.line01 {
    top: 19%;
}

.line02 {
    top: 49%;
}

.line03 {
    top: 79%;
}

.disabled-item {
    cursor: not-allowed;
    filter: grayscale(1);
    opacity: 0.3;
}

.w-mobile {
    display: none;
}

.hamburger-stack.close .line01 {
    background: #ff1d1d;
    transform: rotate(45deg);
    top: 49%;
}

.hamburger-stack.close .line02,
.hamburger-stack.close .line03 {
    background: #ff1d1d;
    transform: rotate(-45deg);
    top: 49%;
}

/* Transitions */
.v-enter-active,
.v-leave-active {
    transition: opacity 0.3s ease;
}

.v-enter-from,
.v-leave-to {
    opacity: 0;
}

/* Large devices (laptops/desktops, 992px and up) */
@media only screen and (max-width: 992px) {
    .bug {
        display: none;
    }
    .menu-icon {
        width: 36px;
    }
}

/* Large devices (laptops/desktops, 992px and up) */
@media only screen and (max-width: 760px) {
    .swv-modal-title {
        line-height: 100%;
    }
    .swv-dark .swv-button {
        line-height: 12px;
    }
    .w-desktop {
        display: none;
    }
    .header-items-left {
        display: none;
    }
    .w-mobile {
        width: 100vw;
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        padding: 0 10px;
    }
    header {
        padding: 20px;
        left: 4px;
        top: 3px;
    }
    .swv-mobile-wallet {
        display: block;
        position: absolute;
        bottom: 10px;
    }
    .menu {
        position: fixed;
        width: 100vw;
        height: 100vh;
        top: 0;
        left: 0;
        z-index: 1;
        pointer-events: all;
        background: #000000aa;
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);
    }
    .hamburger {
        z-index: 99;
    }
    .hamburger-stack {
        z-index: 99;
    }
    .menu-select {
        color: #ff3336;
    }
    .privacy-policy {
        font-size: 12px;
        font-family: 'Bicyclette Bold', Helvetica, Arial;
        text-transform: uppercase;
        color: #ff3336;
    }
}
</style>
